import { debtorDisplayName } from './debtorIdentity.js';
import { debtorCountryLabel } from './debtorCountry.js';
import { formatMatterMoney, formatMattersMoneySum } from './matterCurrency.js';
import {
  loadPersistedSort,
  savePersistedSort,
  sortRowsByColumn,
} from './listColumnSort.js';
import { isPaidStatus } from './paidBeforeAssignment.js';

const STORAGE_KEY = 'demandjet.debtorsListColumns';
const SORT_STORAGE_KEY = 'demandjet.debtorsListSort';

export const DEBTOR_LIST_COLUMNS = Object.freeze([
  {
    id: 'debtorName',
    label: 'Debtor',
    required: true,
    defaultVisible: true,
    sortType: 'string',
  },
  {
    id: 'company',
    label: 'Company',
    defaultVisible: true,
    sortType: 'string',
  },
  {
    id: 'accountNumber',
    label: 'Account #',
    defaultVisible: true,
    sortType: 'string',
  },
  {
    id: 'email',
    label: 'Email',
    defaultVisible: false,
    sortType: 'string',
  },
  {
    id: 'street',
    label: 'Street',
    defaultVisible: false,
    sortType: 'string',
  },
  {
    id: 'city',
    label: 'City',
    defaultVisible: false,
    sortType: 'string',
  },
  {
    id: 'state',
    label: 'State',
    defaultVisible: true,
    sortType: 'string',
  },
  {
    id: 'postal',
    label: 'Postal code',
    defaultVisible: false,
    sortType: 'string',
  },
  {
    id: 'country',
    label: 'Country',
    defaultVisible: false,
    sortType: 'string',
  },
  {
    id: 'openCount',
    label: 'Open demands',
    defaultVisible: true,
    sortType: 'number',
  },
  {
    id: 'demands',
    label: 'Total demands',
    defaultVisible: false,
    sortType: 'number',
  },
  {
    id: 'totalOwed',
    label: 'Total owed',
    defaultVisible: true,
    sortType: 'number',
  },
  {
    id: 'recovered',
    label: 'Recovered',
    defaultVisible: true,
    sortType: 'number',
  },
]);

export const DEFAULT_DEBTOR_SORT = Object.freeze({
  columnId: 'debtorName',
  direction: 'asc',
});

export function debtorSortFieldLabel(columnId) {
  return DEBTOR_LIST_COLUMNS.find((c) => c.id === columnId)?.label || columnId;
}

export function isValidDebtorSortColumn(columnId) {
  return DEBTOR_LIST_COLUMNS.some((c) => c.id === columnId);
}

export function loadDebtorListSort() {
  return loadPersistedSort(SORT_STORAGE_KEY, DEFAULT_DEBTOR_SORT, isValidDebtorSortColumn);
}

export function saveDebtorListSort(sort) {
  return savePersistedSort(
    SORT_STORAGE_KEY,
    sort,
    DEFAULT_DEBTOR_SORT,
    isValidDebtorSortColumn
  );
}

function debtorSortRawValue(debtor, columnId) {
  switch (columnId) {
    case 'debtorName':
      return String(
        debtorDisplayName({
          Debtor_Name: debtor?.name,
          Debtor_Company: debtor?.company,
        }) ||
          debtor?.name ||
          debtor?.company ||
          ''
      ).toLowerCase();
    case 'company':
      return String(debtor?.company || debtor?.name || '').toLowerCase();
    case 'accountNumber':
      return String(debtor?.accountNumber || '').toLowerCase();
    case 'email':
      return String(debtor?.email || '').toLowerCase();
    case 'street':
      return String(debtor?.street || '').toLowerCase();
    case 'city':
      return String(debtor?.city || '').toLowerCase();
    case 'state':
      return String(debtor?.state || '').toLowerCase();
    case 'postal':
      return String(debtor?.postalCode || '').toLowerCase();
    case 'country':
      return String(
        debtorCountryLabel(debtor?.country) || debtor?.country || ''
      ).toLowerCase();
    case 'openCount':
      return Number(debtor?.openCount) || 0;
    case 'demands':
      return Array.isArray(debtor?.matters) ? debtor.matters.length : 0;
    case 'totalOwed':
      return Number(debtor?.totalOwed) || 0;
    case 'recovered':
      return Number(debtor?.recovered) || 0;
    default:
      return '';
  }
}

export function sortDebtors(debtors = [], sort = DEFAULT_DEBTOR_SORT) {
  return sortRowsByColumn(
    debtors,
    {
      columnId: sort?.columnId || DEFAULT_DEBTOR_SORT.columnId,
      direction: sort?.direction,
    },
    debtorSortRawValue,
    (debtor) => String(debtor?.id || '')
  );
}

function formatMoney(value, currencyOrMatter = 'USD') {
  return formatMatterMoney(value, currencyOrMatter);
}

function formatDebtorTotal(debtor, mode) {
  const matters = Array.isArray(debtor?.matters) ? debtor.matters : [];
  if (matters.length) {
    if (mode === 'recovered') {
      return formatMattersMoneySum(
        matters.filter((matter) => isPaidStatus(matter.Status)),
        (matter) => matter.Amount_Owed
      );
    }
    return formatMattersMoneySum(matters, (matter) => matter.Amount_Owed);
  }
  return formatMoney(mode === 'recovered' ? debtor?.recovered : debtor?.totalOwed);
}

export function defaultVisibleDebtorColumnIds() {
  return DEBTOR_LIST_COLUMNS.filter((c) => c.defaultVisible || c.required).map((c) => c.id);
}

export function loadVisibleDebtorColumnIds() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultVisibleDebtorColumnIds();
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return defaultVisibleDebtorColumnIds();
    const allowed = new Set(DEBTOR_LIST_COLUMNS.map((c) => c.id));
    const next = parsed.filter((id) => allowed.has(id));
    for (const col of DEBTOR_LIST_COLUMNS) {
      if (col.required && !next.includes(col.id)) next.unshift(col.id);
    }
    return next.length ? next : defaultVisibleDebtorColumnIds();
  } catch {
    return defaultVisibleDebtorColumnIds();
  }
}

export function saveVisibleDebtorColumnIds(ids) {
  const allowed = new Set(DEBTOR_LIST_COLUMNS.map((c) => c.id));
  const next = (Array.isArray(ids) ? ids : []).filter((id) => allowed.has(id));
  for (const col of DEBTOR_LIST_COLUMNS) {
    if (col.required && !next.includes(col.id)) next.unshift(col.id);
  }
  localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  return next;
}

export function debtorCellValue(debtor, columnId) {
  switch (columnId) {
    case 'debtorName':
      return (
        debtorDisplayName({
          Debtor_Name: debtor?.name,
          Debtor_Company: debtor?.company,
        }) ||
        debtor?.name ||
        '—'
      );
    case 'company':
      return debtor?.company || '—';
    case 'accountNumber':
      return debtor?.accountNumber || '—';
    case 'email':
      return debtor?.email || '—';
    case 'street':
      return debtor?.street || '—';
    case 'city':
      return debtor?.city || '—';
    case 'state':
      return debtor?.state || '—';
    case 'postal':
      return debtor?.postalCode || '—';
    case 'country':
      return debtorCountryLabel(debtor?.country) || debtor?.country || '—';
    case 'openCount':
      return String(Number(debtor?.openCount) || 0);
    case 'demands':
      return String((debtor?.matters || []).length);
    case 'totalOwed':
      return formatDebtorTotal(debtor, 'totalOwed');
    case 'recovered':
      return formatDebtorTotal(debtor, 'recovered');
    default:
      return '—';
  }
}

export function visibleDebtorColumns(visibleIds) {
  const ids = Array.isArray(visibleIds) ? visibleIds : defaultVisibleDebtorColumnIds();
  return DEBTOR_LIST_COLUMNS.filter((col) => ids.includes(col.id));
}

const EXPORT_COLUMNS = Object.freeze([
  { id: 'debtorName', label: 'Debtor' },
  { id: 'company', label: 'Company' },
  { id: 'accountNumber', label: 'Account #' },
  { id: 'email', label: 'Email' },
  { id: 'street', label: 'Street' },
  { id: 'city', label: 'City' },
  { id: 'state', label: 'State' },
  { id: 'postal', label: 'Postal code' },
  { id: 'country', label: 'Country' },
  { id: 'openCount', label: 'Open demands' },
  { id: 'demands', label: 'Total demands' },
  { id: 'totalOwed', label: 'Total owed' },
  { id: 'recovered', label: 'Recovered' },
]);

function csvEscape(value) {
  const text = String(value ?? '');
  if (/[",\n]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
  return text;
}

export function debtorsToCsv(debtors = []) {
  const rows = debtors || [];
  const header = EXPORT_COLUMNS.map((col) => csvEscape(col.label)).join(',');
  const lines = rows.map((debtor) =>
    EXPORT_COLUMNS.map((col) => csvEscape(debtorCellValue(debtor, col.id))).join(',')
  );
  return [header, ...lines].join('\n');
}

export function exportDebtorsCsv(debtors = [], filename) {
  const rows = debtors || [];
  const csv = debtorsToCsv(rows);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  const stamp = new Date().toISOString().slice(0, 10);
  anchor.href = url;
  anchor.download = filename || `demandjet-debtors-${stamp}.csv`;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
  return rows.length;
}
